import { forwardRef } from 'react';
import styled from '@emotion/styled';
import type { ProgressWidgetChart as ProgressWidgetChartData } from '../../hooks/useProgressWidgetCustomizations';
import { ProgressBarWidget } from './ProgressBarWidget';
import { SemiCircleProgressWidget } from './SemiCircleProgressWidget';

type Props = {
  chart: ProgressWidgetChartData;
  /** Theme palette, used for any value that doesn't have an explicit color. */
  themeColors: string[];
};

/** Renders a progress bar or semi-circle quick chart, resolving colors from the theme when the chart has none. */
export const ProgressWidgetChart = forwardRef<HTMLDivElement, Props>(({ chart, themeColors }, ref) => {
  const colors = chart.colors ?? (chart.values ? chart.values.map((_, i) => themeColors[i % themeColors.length]) : []);
  const Widget = chart.type === 'progress-bar-widget' ? ProgressBarWidget : SemiCircleProgressWidget;

  return (
    <Container>
      <Widget
        ref={ref}
        title={chart.title}
        backgroundColor={chart.backgroundColor}
        values={chart.values ?? []}
        labels={chart.labels ?? []}
        colors={colors}
        minValues={chart.minValues ?? []}
        maxValues={chart.maxValues ?? []}
        units={chart.units}
        insights={chart.insights}
      />
    </Container>
  );
});

ProgressWidgetChart.displayName = 'ProgressWidgetChart';

const Container = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
  minWidth: 0, // Allow flex item to shrink below its minimum content size
  '& > *': {
    maxWidth: '100%',
    minWidth: 0,
    flex: '0 1 auto', // Allow shrinking
  },
});
